import { extractOppgaveReturUrlFraPathname } from '@components/oppgave/utils/oppgave-url-utils';

import { HttpStatusCode, type VerktoyProxyErrorResponse } from '../types/apiTypes';
import type { Verktoy } from '../types/minhelseentities';
import type { HNVerktoyFrontend } from '../types/Resources';
import type { VerktoyFilterState } from '../types/stateTypes';
import { PageRoutes } from '../types/types';

import type { BreadcrumbLink } from '@helsenorge/core-framework/breadcrumb';
import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';
import { HelsenorgeHttpError, HelsenorgeProxyError } from '@helsenorge/framework-utils/hn-proxy-service';

import { getQueryURLFromState } from './verktoyFilters';

export const isHelsenorgeProxyError = (error: HelsenorgeProxyError | HelsenorgeHttpError | Error): error is HelsenorgeProxyError => {
  return error instanceof HelsenorgeProxyError;
};

export const isHelsenorgeHttpError = (error: HelsenorgeProxyError | HelsenorgeHttpError | Error): error is HelsenorgeHttpError => {
  return error instanceof HelsenorgeHttpError;
};

export const getErrorLabel = (resources: HNVerktoyFrontend, error: VerktoyProxyErrorResponse, pathname?: string): string => {
  const genericErrorText = resources.verktoyErrorGeneric || 'feil';

  if (isHelsenorgeProxyError(error) || isHelsenorgeHttpError(error)) {
    if (error.statusCode) {
      const httpStatusCode = error.statusCode.toString();
      if (httpStatusCode === HttpStatusCode.not_found && pathname && isLocationVerktoyDetailsPage(pathname)) {
        return resources.verktoyDetailsPage_invalidGuid;
      }
    }
  }
  return error.message || genericErrorText;
};

export const hasPageHeader = (pathname: string): boolean => {
  return pathname === PageRoutes.entryPage;
};

export const userHasLaeringsaktivitet = (verktoyMedLaeringsaktivitet: Verktoy[]): boolean => {
  return verktoyMedLaeringsaktivitet?.length > 0;
};

export const getReturnFromDetailsPageUrl = (pathname: string, enteredDetailsPageFrom: string, from?: string): string => {
  const verktoyIdParam = extractVerktoyIdFromPathname(pathname, 1);

  if (enteredDetailsPageFrom === '' && from) {
    enteredDetailsPageFrom = from;
  }

  const extraPath = verktoyIdParam ? `/${verktoyIdParam}` : '';
  switch (enteredDetailsPageFrom) {
    case PageRoutes.finnVerktoy:
      return `${PageRoutes.finnVerktoy}${extraPath}`;
    case PageRoutes.entryPage:
      return `${PageRoutes.entryPage}`;
    case PageRoutes.oppgave: {
      return getBreadCrumbLinkTilOppgaveMelding();
    }
    default:
      return `${PageRoutes.finnVerktoy}`;
  }
};

export const getDetailsPageBreadCrumbText = (
  enteredDetailsPageFrom: string,
  resources: HNVerktoyFrontend,
  navigatedFrom?: string
): string => {
  if (enteredDetailsPageFrom === PageRoutes.oppgave) {
    return resources.innboks_breadcrumb;
  }

  const prevousRoute = navigatedFrom || enteredDetailsPageFrom;
  switch (prevousRoute) {
    case PageRoutes.finnVerktoy:
      return resources.finnVerktoy_header || 'Finn selvhjelpsverktøy';
    case PageRoutes.oppgave:
      return resources.innboks_breadcrumb || 'Innboks';
    default:
      return resources.entryPage_header || 'Verktøy';
  }
};

export const isLocationVerktoyDetailsPage = (pathname: string): boolean => {
  return pathname !== PageRoutes.entryPage && !pathname.startsWith(PageRoutes.finnVerktoy);
};

export const isLocationEntryPage = (pathname: string): boolean => {
  return pathname === PageRoutes.entryPage;
};

export const getBreadCrumbLink = (
  pathname: string,
  verktoyFilterState: VerktoyFilterState,
  enteredDetailsPageFrom: string,
  resources: HNVerktoyFrontend,
  error: VerktoyProxyErrorResponse | undefined,
  from?: string
): BreadcrumbLink | undefined => {
  if (pathname === PageRoutes.entryPage) {
    return undefined;
  }

  if (pathname !== PageRoutes.entryPage && !pathname.includes(PageRoutes.finnVerktoy)) {
    const returnUrl = getReturnFromDetailsPageUrl(pathname, enteredDetailsPageFrom, from);
    const urlWithFilters = returnUrl + getQueryPartURLFromFilterState(verktoyFilterState);

    return {
      url: error ? `${PageRoutes.baseUrl}${PageRoutes.finnVerktoy}` : urlWithFilters,
      name: getDetailsPageBreadCrumbText(enteredDetailsPageFrom, resources, from),
      renderAsRouterLink: !error,
    };
  }

  return {
    url: PageRoutes.entryPage,
    name: resources.entryPage_header || 'Verktøy',
    renderAsRouterLink: true,
  };
};

export const getQueryPartURLFromFilterState = (verktoyFilterState: VerktoyFilterState): string => {
  if (verktoyFilterState === undefined) {
    return '';
  }
  const queryParamPart = getQueryURLFromState(false, verktoyFilterState);
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(queryParamPart)) {
    if (Array.isArray(value)) {
      value.forEach(val => params.append(key, val));
    } else {
      params.append(key, value);
    }
  }
  const queryString = params.toString();
  return queryString ? '?' + queryString : '';
};

export const extractVerktoyIdFromPathname = (pathname: string, partToExtract: number): string | null => {
  const parts = pathname.split('/');
  return parts.length > 1 ? parts[partToExtract] : null;
};

export const shouldShowDeleteButton = (erLaeringsaktivitet: boolean, enteredDetailsPageFrom: string): boolean => {
  return isAuthorized() && erLaeringsaktivitet && enteredDetailsPageFrom !== PageRoutes.oppgave;
};

const getBreadCrumbLinkTilOppgaveMelding = (): string => {
  const retururl = extractOppgaveReturUrlFraPathname();
  return retururl ? `${window.location.origin}${retururl}` : retururl;
};
